import React, { useCallback, useRef, useState } from "react"
import { AnimatePresence } from "framer-motion"
import { useTranslation } from "react-i18next"
import FormFieldWrapper from "./FormFieldWrapper"
import FormCityField from "./FormCityField"
import FormSwapCityButton from "./FormSwapCityButton"
import SearchSuggestionsModal from "./SearchSuggestionsModal"
import { useClickOutside } from "../../../hooks"
import PlaneImg from "../../../../assets/avia/search/plane.png"

const FROM_FIELD = "from"
const TO_FIELD = "to"

const formatLabel = (item) => {
  if (!item) return ""

  switch (item.type) {
    case "hotel":
      return `${item.name}, ${item.city}`
    case "station":
      return `${item.name} (${item.city})`
    default:
      return item.city || item.name
  }
}

const normalizeSelected = (item) => {
  if (!item) return []
  return [
    {
      ...item,
      city: formatLabel(item),
    },
  ]
}

const Form = ({
  departureTerminals = [],
  arrivalTerminals = [],
  selectedFrom = null,
  selectedTo = null,
  isLoading = false,
  isLoadingTo = false,
  onSearch,
  onFetchDefault,
  onSelect,
  onSwap,
  onClear,
  contents,
}) => {
  const { t } = useTranslation()

  const [activeField, setActiveField] = useState(null)
  const [fromQuery, setFromQuery] = useState("")
  const [toQuery, setToQuery] = useState("")
  const [showModal, setShowModal] = useState(false)

  const modalRef = useRef(null)
  const debounceRef = useRef(null)

  useClickOutside(modalRef, () => {
    setShowModal(false)
    setActiveField(null)
    setFromQuery("")
    setToQuery("")
  })

  const activeTerminals = activeField === FROM_FIELD ? departureTerminals : arrivalTerminals
  const activeIsLoading = activeField === FROM_FIELD ? isLoading : isLoadingTo
  const currentQuery = activeField === FROM_FIELD ? fromQuery : toQuery

  const handleFocus = (field) => {
    setActiveField(field)
    setShowModal(true)
    onFetchDefault?.(field)
  }

  const handleInputChange = useCallback(
    (value, field) => {
      if (field === FROM_FIELD) setFromQuery(value)
      else setToQuery(value)

      clearTimeout(debounceRef.current)
      debounceRef.current = setTimeout(() => {
        if (!value || value.trim().length < 2) onFetchDefault?.(field)
        else onSearch?.(value, field)
      }, 300)
    },
    [onSearch, onFetchDefault]
  )

  const handleQueryChange = useCallback(
    (value) => handleInputChange(value, activeField),
    [activeField, handleInputChange]
  )

  const handleSelect = useCallback(
    (item) => {
      onSelect?.(item, activeField)
      if (activeField === FROM_FIELD) setFromQuery("")
      else setToQuery("")
      setShowModal(false)
    },
    [activeField, onSelect]
  )

  const handleSwap = () => {
    onSwap?.()
    setFromQuery("")
    setToQuery("")
  }

  return (
    <div className="flex flex-col sm:flex-row bg-white rounded-md shadow-md p-2 w-full overflow-visible relative gap-2 sm:gap-4 sm:items-center sm:h-[53px]">
      <FormFieldWrapper t={t}>
        <FormCityField
          planeIcon={PlaneImg}
          label={t("admin.departure_from_v3")}
          terminals={normalizeSelected(selectedFrom)}
          onClear={() => onClear?.("from")}
          placeholder={t(contents.from_placeholder)}
          rotated
          handleInputFocus={() => handleFocus(FROM_FIELD)}
          handleInputChange={(value) => handleInputChange(value, FROM_FIELD)}
          valueInput={fromQuery}
        />
      </FormFieldWrapper>

      <div className="flex justify-center shrink-0">
        <FormSwapCityButton onClick={handleSwap} />
      </div>

      <FormFieldWrapper isFrom={false} t={t}>
        <FormCityField
          label={t("filter.to_where")}
          terminals={normalizeSelected(selectedTo)}
          onClear={() => onClear?.("to")}
          placeholder={t(contents.to_placeholder)}
          handleInputFocus={() => handleFocus(TO_FIELD)}
          handleInputChange={(value) => handleInputChange(value, TO_FIELD)}
          valueInput={toQuery}
        />
      </FormFieldWrapper>

      <AnimatePresence>
        {showModal && (
          <SearchSuggestionsModal
            ref={modalRef}
            activeField={activeField}
            destinations={activeTerminals}
            isLoading={activeIsLoading}
            query={currentQuery}
            selectedFrom={selectedFrom}
            selectedTo={selectedTo}
            onQueryChange={handleQueryChange}
            onSelect={handleSelect}
            onClear={onClear}
            onClearTerminals={onClear}
            onClose={() => {
              setShowModal(false)
              setActiveField(null)
              setFromQuery("")
              setToQuery("")
            }}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

export default Form
